import { mustParse, parseCellId, toCellId, type Point } from './geometry.ts';
import { CLASSIC_DEPLOYMENT } from './ruleset.ts';
import { SIDES, type CellId, type CellType, type Deployment, type MapDef, type Side } from './types.ts';

/** Characters of `MapDef.rows` (R-MAP-08). */
export const CELL_CHARS: Readonly<Record<string, CellType>> = {
  '.': 'sea',
  ',': 'coast',
  '#': 'land',
  '*': 'reef',
  p: 'port',
  R: 'royal_port',
  F: 'land',
};

/** R-MAP-03 base capacities. */
export const CELL_CAPACITY: Readonly<Record<CellType, number>> = {
  sea: 2,
  coast: 1,
  land: 0,
  reef: 0,
  port: 2,
  royal_port: 2,
};

export interface MapIssue {
  readonly rule: string;
  readonly message: string;
}

export class MapValidationError extends Error {
  readonly issues: readonly MapIssue[];

  constructor(issues: readonly MapIssue[]) {
    super(`Invalid map: ${issues.map((i) => `[${i.rule}] ${i.message}`).join('; ')}`);
    this.issues = issues;
    this.name = 'MapValidationError';
  }
}

export interface ValidateMapOptions {
  /** Check the per-side composition needed for random setup (R-MAP-07). Default true. */
  readonly requireComposition?: boolean;
  /** Port quotas the composition check sizes ports for (R-SETUP-02). Default: the classic 4/5/6. */
  readonly deployment?: Deployment;
}

const isNeighbour = (a: Point, b: Point) => Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y)) === 1;

/** Structural and semantic validation of a map document. Returns all issues found. */
export function validateMap(input: unknown, options: ValidateMapOptions = {}): MapIssue[] {
  const issues: MapIssue[] = [];
  const deployment = options.deployment ?? CLASSIC_DEPLOYMENT;
  const fail = (message: string, rule = 'R-MAP-08') => issues.push({ rule, message });

  if (typeof input !== 'object' || input === null) {
    fail('map must be an object');
    return issues;
  }
  const map = input as Partial<MapDef>;
  if (typeof map.id !== 'string' || map.id === '') fail('id must be a non-empty string');
  if (typeof map.name !== 'string') fail('name must be a string');
  const { width, height, rows } = map;
  if (!Number.isInteger(width) || (width as number) < 1) fail('width must be a positive integer');
  if (!Number.isInteger(height) || (height as number) < 1) {
    fail('height must be a positive integer');
  }
  if (!Array.isArray(rows)) fail('rows must be an array of strings');
  if (!Array.isArray(map.forts)) fail('forts must be an array');
  const royalPorts: unknown = map.royalPorts;
  if (typeof royalPorts !== 'object' || royalPorts === null) {
    fail('royalPorts must be an object');
  }
  if (issues.length > 0) return issues;

  const w = width as number;
  const h = height as number;
  const grid = rows as readonly unknown[];
  if (grid.length !== h) fail(`rows has ${grid.length} entries, expected ${h}`, 'R-MAP-01');
  grid.forEach((row, y) => {
    if (typeof row !== 'string' || row.length !== w) {
      fail(`row ${y + 1} must be a string of length ${w}`, 'R-MAP-01');
      return;
    }
    for (const ch of row) {
      if (!(ch in CELL_CHARS)) fail(`row ${y + 1}: unknown cell character '${ch}'`, 'R-MAP-03');
    }
  });
  if (issues.length > 0) return issues;

  const charAt = (p: Point) => (grid[p.y] as string)[p.x] as string;
  const inBounds = (p: Point) => p.x >= 0 && p.y >= 0 && p.x < w && p.y < h;
  const cell = (id: unknown, what: string): Point | null => {
    const p = typeof id === 'string' ? parseCellId(id) : null;
    if (!p || !inBounds(p)) {
      fail(`${what}: '${String(id)}' is not a cell of this map`, 'R-MAP-02');
      return null;
    }
    return p;
  };

  // Royal ports
  const royal = map.royalPorts as Record<string, unknown>;
  let royalCount = 0;
  for (const [side, id] of Object.entries(royal)) {
    if (!(SIDES as readonly string[]).includes(side)) {
      fail(`royalPorts: unknown side '${side}'`);
      continue;
    }
    const p = cell(id, `royal port of ${side}`);
    if (p && charAt(p) !== 'R') fail(`royal port ${String(id)} must be marked 'R'`, 'R-MAP-07');
    royalCount++;
  }

  // Forts and ports
  const fortIds = new Set<string>();
  const fortCells = new Set<string>();
  const portCellOwner = new Map<string, string>();
  const perSide: Record<Side, { first: number; second: number }> = {
    red: { first: 0, second: 0 },
    blue: { first: 0, second: 0 },
  };
  for (const [i, fort] of (map.forts as readonly unknown[]).entries()) {
    const f = fort as Partial<MapDef['forts'][number]> | null;
    const label = `forts[${i}]`;
    if (typeof f !== 'object' || f === null) {
      fail(`${label} must be an object`);
      continue;
    }
    if (typeof f.id !== 'string' || f.id === '') fail(`${label}.id must be a non-empty string`);
    else if (fortIds.has(f.id)) fail(`duplicate fort id '${f.id}'`);
    else fortIds.add(f.id);
    if (!(SIDES as readonly string[]).includes(f.side as string)) fail(`${label}.side is invalid`);
    if (f.class !== 'first_line' && f.class !== 'second_line') fail(`${label}.class is invalid`);
    else if (f.side === 'red' || f.side === 'blue') {
      perSide[f.side][f.class === 'first_line' ? 'first' : 'second']++;
    }
    const fp = cell(f.cell, `${label}.cell`);
    if (fp) {
      if (charAt(fp) !== 'F') fail(`${label}.cell ${String(f.cell)} must be marked 'F'`, 'R-MAP-05');
      fortCells.add(toCellId(fp));
    }
    if (!Array.isArray(f.portCells) || f.portCells.length === 0) {
      fail(`${label}.portCells must be a non-empty array`, 'R-MAP-05');
      continue;
    }
    const pts: Point[] = [];
    for (const id of f.portCells as unknown[]) {
      const p = cell(id, `${label}.portCells`);
      if (!p) continue;
      const key = toCellId(p);
      if (charAt(p) !== 'p') fail(`port cell ${key} must be marked 'p'`, 'R-MAP-05');
      if (portCellOwner.has(key)) fail(`port cell ${key} belongs to more than one fort`, 'R-MAP-05');
      portCellOwner.set(key, String(f.id));
      pts.push(p);
    }
    if (fp && pts.length > 0 && !pts.some((p) => isNeighbour(p, fp))) {
      fail(`${label}: fort cell must be adjacent to its port`, 'R-MAP-05');
    }
    // Port cells must form one 8-connected group.
    if (pts.length > 1) {
      const seen = new Set([0]);
      const queue = [0];
      while (queue.length > 0) {
        const a = pts[queue.pop() as number] as Point;
        pts.forEach((b, j) => {
          if (!seen.has(j) && isNeighbour(a, b)) {
            seen.add(j);
            queue.push(j);
          }
        });
      }
      if (seen.size !== pts.length) fail(`${label}: port cells must be connected`, 'R-MAP-05');
    }
    if (options.requireComposition !== false) {
      const needed =
        f.class === 'first_line'
          ? deployment.firstLine
          : Math.max(deployment.secondLine, deployment.largePort);
      if (pts.length * CELL_CAPACITY.port < needed) {
        fail(`${label}: port needs at least ${Math.ceil(needed / 2)} cells for setup`, 'R-MAP-08');
      }
    }
  }

  // Every marked cell must be accounted for.
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const ch = charAt({ x, y });
      const id = toCellId({ x, y });
      if (ch === 'p' && !portCellOwner.has(id)) fail(`port cell ${id} has no fort`, 'R-MAP-05');
      if (ch === 'F' && !fortCells.has(id)) fail(`fort cell ${id} has no fort entry`, 'R-MAP-05');
      if (ch === 'R') royalCount--;
    }
  }
  if (royalCount !== 0) fail(`every 'R' cell must be listed in royalPorts`, 'R-MAP-07');

  if (options.requireComposition !== false) {
    if (perSide.red.first !== perSide.blue.first || perSide.red.second !== perSide.blue.second) {
      fail('both sides must have as many first-line and second-line forts', 'R-MAP-07');
    }
    for (const side of SIDES) {
      if (perSide[side].second < 1) fail(`${side} must have a second-line fort`, 'R-MAP-07');
      if (royal[side] === undefined) fail(`${side} must have a royal port`, 'R-MAP-07');
    }
  }
  return issues;
}

/** Precomputed lookup tables for a validated map. */
export interface MapIndex {
  readonly width: number;
  readonly height: number;
  inBounds(p: Point): boolean;
  type(p: Point): CellType;
  isFortCell(p: Point): boolean;
  /** Fort id whose port contains the cell, or null. */
  portOf(p: Point): string | null;
  /** Side whose Royal Port is the cell, or null. */
  royalOf(p: Point): Side | null;
  royalCell(side: Side): Point | null;
}

const indexCache = new WeakMap<MapDef, MapIndex>();

export function indexMap(map: MapDef): MapIndex {
  const cached = indexCache.get(map);
  if (cached) return cached;
  const { width, height } = map;
  const key = (p: Point) => p.y * width + p.x;
  const types: CellType[] = [];
  const fortCell: boolean[] = [];
  map.rows.forEach((row) => {
    for (const ch of row) {
      types.push(CELL_CHARS[ch] ?? 'land');
      fortCell.push(ch === 'F');
    }
  });
  const portOf = new Map<number, string>();
  for (const fort of map.forts) {
    for (const id of fort.portCells) portOf.set(key(mustParse(id)), fort.id);
  }
  const royal = new Map<number, Side>();
  const royalCells: Partial<Record<Side, Point>> = {};
  for (const side of SIDES) {
    const id = map.royalPorts[side];
    if (id !== undefined) {
      const p = mustParse(id);
      royal.set(key(p), side);
      royalCells[side] = p;
    }
  }
  const inBounds = (p: Point) => p.x >= 0 && p.y >= 0 && p.x < width && p.y < height;
  const index: MapIndex = {
    width,
    height,
    inBounds,
    type: (p) => (inBounds(p) ? (types[key(p)] as CellType) : 'land'),
    isFortCell: (p) => inBounds(p) && fortCell[key(p)] === true,
    portOf: (p) => (inBounds(p) ? (portOf.get(key(p)) ?? null) : null),
    royalOf: (p) => (inBounds(p) ? (royal.get(key(p)) ?? null) : null),
    royalCell: (side) => royalCells[side] ?? null,
  };
  indexCache.set(map, index);
  return index;
}

export function allCells(map: MapDef): CellId[] {
  const out: CellId[] = [];
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) out.push(toCellId({ x, y }));
  }
  return out;
}
